import React, { useEffect, useState, useRef } from "react";
import { Typography, Modal, Button, List, message } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  SyncOutlined,
  LoadingOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { useLazyQuery, gql, useMutation } from "@apollo/client";

const { Paragraph } = Typography;

const GET_CLOUD_AGENT_TASK_DETAILS = gql`
  query CloudAgentTaskDetails($taskId: String!) {
    cloudAgentTaskDetails(taskId: $taskId) {
      taskId
      status
      liveUrl
      steps {
        id
        step
        evaluationPreviousGoal
        nextGoal
      }
    }
  }
`;

const PAUSE_AGENT_TASK = gql`
  mutation PauseCloudAgentTask($taskId: String!) {
    pauseCloudAgentTask(taskId: $taskId)
  }
`;

const RESUME_AGENT_TASK = gql`
  mutation ResumeAgentTask($taskId: String!) {
    resumeCloudAgentTask(taskId: $taskId)
  }
`;

const STOP_AGENT_TASK = gql`
  mutation StopAgentTask($taskId: String!) {
    stopCloudAgentTask(taskId: $taskId)
  }
`;

interface AgentLiveModalProps {
  visible: boolean;
  onClose: () => void;
  taskId: string;
  liveUrl: string;
  prompt: string;
  actionType: string;
}

const AgentLiveModal: React.FC<AgentLiveModalProps> = ({
  visible,
  onClose,
  taskId,
  liveUrl,
  prompt,
  actionType
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [steps, setSteps] = useState<any[]>([]);
  const [status, setStatus] = useState<string>("");
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const stepsEndRef = useRef<HTMLDivElement | null>(null);

  const [pauseAgentTask] = useMutation(PAUSE_AGENT_TASK);
  const [resumeAgentTask] = useMutation(RESUME_AGENT_TASK);
  const [stopAgentTask] = useMutation(STOP_AGENT_TASK);

  const [fetchTaskDetails, { data, loading }] = useLazyQuery(
    GET_CLOUD_AGENT_TASK_DETAILS,
    {
      fetchPolicy: "network-only",
    }
  );

  // Setup polling on mount
  useEffect(() => {
    if (visible && taskId) {
      fetchTaskDetails({ variables: { taskId } }); // Initial load

      pollRef.current = setInterval(() => {
        fetchTaskDetails({ variables: { taskId } });
      }, 3000);

      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
  }, [visible, taskId]);

  // Sync task details
  useEffect(() => {
    if (data?.cloudAgentTaskDetails) {
      setSteps(data.cloudAgentTaskDetails.steps);
      setStatus(data.cloudAgentTaskDetails.status);

      // If task is finished/failed/stopped, stop polling
      if (
        ["finished", "failed", "stopped"].includes(
          data.cloudAgentTaskDetails.status
        )
      ) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    }
  }, [data]);

  useEffect(() => {
    if (stepsEndRef.current) {
      stepsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [steps]);

  const handlePause = async () => {
    try {
      await pauseAgentTask({ variables: { taskId } });
      setIsPaused(true);
      message.success("Task paused");
      fetchTaskDetails({ variables: { taskId } });
    } catch (err) {
      message.error("Failed to pause task");
    }
  };

  const handleResume = async () => {
    try {
      await resumeAgentTask({ variables: { taskId } });
      setIsPaused(false);
      message.success("Task resumed");
      fetchTaskDetails({ variables: { taskId } });
    } catch (err) {
      message.error("Failed to resume task");
    }
  };

  const handleStop = async () => {
    try {
      await stopAgentTask({ variables: { taskId } });
      message.success("Task stopped");
      fetchTaskDetails({ variables: { taskId } });
    } catch (err) {
      message.error("Failed to stop task");
    }
  };

  const handleTogglePause = async () => {
    if (isPaused) {
      await handleResume();
    } else {
      await handlePause();
    }
  };

  const handleTakeOver = () => {
    window.open(liveUrl, "_blank");
  };

  // Helper function to get the appropriate icon based on step status
  const getStepStatusIcon = (stepText: string) => {
    if (stepText.includes("Success")) {
      return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />;
    } else if (stepText.includes("Failed")) {
      return <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />;
    } else if (stepText.includes("Unknown")) {
      return <InfoCircleOutlined style={{ color: "#1890ff", fontSize: 18 }} />;
    } else {
      return <LoadingOutlined style={{ color: "#1890ff", fontSize: 18 }} />;
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Agent Live View - Status: </span>
          {status ? (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {status === "created" && (
                <LoadingOutlined style={{ color: "#1890ff" }} />
              )}
              {status === "running" && (
                <SyncOutlined spin style={{ color: "#1890ff" }} />
              )}
              {status === "finished" && (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              )}
              {status === "stopped" && (
                <StopOutlined style={{ color: "#faad14" }} />
              )}
              {status === "paused" && (
                <PauseCircleOutlined style={{ color: "#faad14" }} />
              )}
              {status === "failed" && (
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              <span>{status}</span>
            </span>
          ) : (
            "loading..."
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ top: 20 }}
    >
      <div style={{ display: "flex", gap: "16px" }}>
        {/* Left Column: Steps and Controls */}
        <div
          style={{ flex: 1, borderRight: "1px solid #ddd", paddingRight: 16, overflowY: "auto", maxHeight: "70vh" }}
        >
          <h3>Agent Steps</h3>
          <List
            itemLayout="horizontal"
            dataSource={steps}
            renderItem={(step, index) => {
              // Extract status and action parts from the evaluation
              let evaluationStatus = "";
              let evaluationAction = "";
              
              if (step.evaluationPreviousGoal) {
                const parts = step.evaluationPreviousGoal.split('-');
                if (parts.length > 1) {
                  evaluationStatus = parts[0].trim();
                  evaluationAction = parts.slice(1).join('-').trim();
                } else {
                  evaluationAction = step.evaluationPreviousGoal;
                }
              }

              return (
                <List.Item
                  key={step.id}
                  style={{
                    padding: 16,
                    background: "#f9f9f9",
                    marginBottom: 12,
                    borderRadius: 8,
                    border: "1px solid #eee",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 8 }}>
                      {getStepStatusIcon(evaluationStatus)}
                      <div style={{ marginLeft: 12, flex: 1 }}>
                        <div style={{ fontWeight: "bold" }}>
                          {evaluationStatus ? `${evaluationStatus} - ${evaluationAction}` : "Processing..."}
                        </div>
                      </div>
                    </div>
                    
                    {step.nextGoal && (
                      <div 
                        style={{
                          marginLeft: 30,
                          padding: "8px 12px",
                          background: "#f0f7ff",
                          borderRadius: 6,
                          borderLeft: "3px solid #1890ff"
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>Next Action:</span> {step.nextGoal}
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
          <div ref={stepsEndRef} />

          <div
            style={{
              position: "sticky",
              bottom: 0,
              marginTop: 20,
              padding: 16,
              borderRadius: 8,
              background: "#fafafa",
              display: "flex",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <Button
              type={isPaused ? "default" : "primary"}
              icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={handleTogglePause}
              disabled={!["running", "paused"].includes(status)}
              size="large"
            >
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button 
              danger
              icon={<StopOutlined />} 
              onClick={handleStop}
              size="large"
            >
              Stop
            </Button>
          </div>
        </div>

        {/* Right Column: Live Iframe */}
        <div style={{ flex: 2, position: "relative" }}>
          <iframe
            src={liveUrl}
            style={{
              width: "100%",
              height: "70vh",
              border: "1px solid #eee",
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
            }}
            title="Agent Live Preview"
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "rgba(0, 0, 0, 0.1)",
              borderRadius: 8,
              zIndex: 1000,
            }}
          >
            <Button
              type="primary"
              onClick={handleTakeOver}
              size="large"
              style={{
                fontSize: 16,
                height: "auto",
                padding: "12px 24px",
                opacity: 0.7
              }}
            >
              Take Over
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AgentLiveModal;